/**
 * SetCardsGrid - Grid view for displaying cards in a set with ownership highlighting
 *
 * Displays MinimalCard data as image thumbnails with:
 * - Ownership badge (green, shows quantity)
 * - Green glow/border for owned cards
 * - Rarity indicator
 * - Hover preview
 *
 * Performance note: For sets with 500+ cards, consider adding virtualization
 * using @tanstack/react-virtual if performance becomes an issue.
 */
import clsx from 'clsx';
import { memo, type ReactElement } from 'react';
import { IoCheckmark } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import type { MinimalCard } from '@/services/cardDatabase';
import type { Rarity } from '@/types/card';

export interface SetCardsGridProps {
  cards: MinimalCard[];
  /** Map of scryfall_id -> owned quantity */
  ownedQuantities: Map<string, number>;
  /** Called when a card is clicked */
  onSelectCard?: (scryfallId: string) => void;
  /** Currently selected card ID */
  selectedCardId?: string | null;
}

/**
 * Get rarity indicator color class
 */
function getRarityColorClass(rarity: Rarity | undefined): string {
  switch (rarity) {
    case 'common':
      return 'bg-silver';
    case 'uncommon':
      return 'bg-uncommon';
    case 'rare':
      return 'bg-gold';
    case 'mythic':
      return 'bg-mythic';
    case 'special':
    case 'bonus':
      return 'bg-aether';
    default:
      return 'bg-silver';
  }
}

/**
 * Get rarity label for accessibility
 */
function getRarityLabel(rarity: Rarity | undefined): string {
  if (!rarity) return 'Unknown';
  return rarity.charAt(0).toUpperCase() + rarity.slice(1);
}

export default function SetCardsGrid({ cards, ownedQuantities, onSelectCard, selectedCardId }: SetCardsGridProps): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
      {cards.map((card) => (
        <SetCardGridItem
          key={card.id}
          card={card}
          ownedQuantity={ownedQuantities.get(card.id) ?? 0}
          isSelected={selectedCardId === card.id}
          onSelect={onSelectCard}
        />
      ))}
    </div>
  );
}

interface SetCardGridItemProps {
  card: MinimalCard;
  ownedQuantity: number;
  isSelected: boolean;
  onSelect?: (scryfallId: string) => void;
}

/**
 * Individual grid card - memoized to prevent unnecessary re-renders
 */
const SetCardGridItem = memo(function SetCardGridItem({ card, ownedQuantity, isSelected, onSelect }: SetCardGridItemProps): ReactElement {
  const isOwned = ownedQuantity > 0;

  const handleClick = () => {
    onSelect?.(card.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(card.id);
    }
  };

  return (
    <CardHoverPreview imageUris={card.image_uris} name={card.name}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={clsx(
          'group relative cursor-pointer rounded-lg transition-all',
          'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
          isSelected && 'ring-aether ring-2',
          isOwned && 'shadow-[0_0_12px_rgba(16,185,129,0.4)] ring-2 ring-emerald-500/60',
        )}
        aria-label={`${card.name}${isOwned ? `, owned: ${ownedQuantity}` : ''}`}
      >
        <CardImage
          imageUris={card.image_uris}
          name={card.name}
          size="small"
          sourceSize="normal"
          className="pointer-events-none h-auto w-full rounded-lg"
        />

        {/* Ownership badge - green with quantity */}
        {isOwned && (
          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-white shadow-md">
            <IoCheckmark className="h-3 w-3" />
            <span className="text-xs font-semibold">{ownedQuantity}</span>
          </div>
        )}

        {/* Rarity indicator - small dot */}
        <div
          className={clsx('absolute bottom-1.5 left-1.5 h-2.5 w-2.5 rounded-full shadow-sm', getRarityColorClass(card.rarity))}
          title={getRarityLabel(card.rarity)}
        />

        {/* Hover overlay with card name */}
        <div className="bg-void/70 pointer-events-none absolute inset-0 flex items-end justify-center rounded-lg pb-3 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="text-pearl line-clamp-2 px-2 text-center text-xs font-medium drop-shadow">{card.name}</span>
        </div>
      </div>
    </CardHoverPreview>
  );
});
